import {
  getSavedRegion,
  saveRegion
} from "../services/addinStorage.js";

import {
  detectInitialLanguage,
  setLanguage,
  getLanguage,
  getLanguageLabels,
  t,
  localizeElementText,
  localizeElementPlaceholder,
  localizeSelectOption
} from "../i18n/index.js";

import {
  getComposeContext,
  setBodyHtml,
  setSubjectIfEmpty,
  getSelectedTextAsync,
  replaceSelectedTextAsync,
  replaceBodyHtmlAsync,
  extractEditableDraftOnly,
  getComposeBodyTextAsync
} from "../utils/officeAsync.js";

import { improveEmailText } from "../services/improveEmail.js";

import { createEmailDraft, getExtensionTemplates } from "../services/createDraft.js";

import {
  getCachedSessionBundle,
  logoutSession,
  requestOutlookSigninCode,
  verifyOutlookSigninCode,
  getOfficeMailboxProfile
} from "../services/session.js";

import { REGIONS } from "../services/regions.js";

const APP_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

let pendingOtp = null;
let sessionBundle = null;
let magicTimer = null;
let statusClearTimer = null;

function removeElement(el) {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

function setHidden(el, hidden) {
  if (!el) return;

  if (hidden) {
    el.classList.add("hidden");
  } else {
    el.classList.remove("hidden");
  }
}

function showBootError(message, detail = {}) {
  const el = document.getElementById("bootError");

  if (el) {
    el.textContent = `Startup error: ${message || "Unknown error"}`;
    el.classList.remove("hidden");
  }

  const debug = document.getElementById("welcomeDebug");
  if (debug) {
    debug.textContent = JSON.stringify(
      {
        message,
        ...detail,
        userAgent: navigator.userAgent,
        href: window.location.href
      },
      null,
      2
    );
    debug.classList.remove("hidden");
  }
}

async function boot() {
  try {
    if (!window.Office?.onReady) {
      throw new Error("Office.js did not load.");
    }

    await window.Office.onReady();
    await init();
  } catch (err) {
    console.error("Snoooz startup failed:", err);

    showBootError(err?.message || String(err), {
      stack: err?.stack
    });
  }
}

boot();

function renderAppVersion() {
  const el = document.getElementById("appVersion");
  if (el) el.textContent = APP_VERSION;
}

async function init() {
  try {
    bindEvents();
    renderAppVersion();

    setLanguage(detectInitialLanguage());
    renderLanguageSwitcher();
    applyTranslations();

    if (import.meta.env.PROD) {
      removeElement(document.getElementById("debug"));
    }

    sessionBundle = await getCachedSessionBundle();

    if (!sessionBundle?.state?.session) {
      showWelcome();
      return;
    }

    await showDraftView(sessionBundle);
  } catch (err) {
    console.error("Snoooz init failed:", err);

    showBootError(err?.message || String(err), {
      stack: err?.stack
    });

    showWelcome();
  }
}

function renderLanguageSwitcher() {
  const select = document.getElementById("language");
  if (!select) return;

  const labels = getLanguageLabels();
  select.innerHTML = "";

  for (const code in labels) {
    if (Object.prototype.hasOwnProperty.call(labels, code)) {
      const option = document.createElement("option");
      option.value = code;
      option.text = labels[code];
      select.appendChild(option);
    }
  }

  select.value = getLanguage();
}

function applyTranslations() {
  localizeElementText("appTagline", "appTagline");
  localizeElementText("languageLabel", "language");
  localizeElementText("regionLabel", "region");
  //localizeElementText("regionTitle", "preferences");
  localizeElementText("regionNote", "regionNote");
  localizeSelectOption("region", "us", "regionUs");
  localizeSelectOption("region", "eu", "regionEu");
  localizeElementText("versionLabel", "version");
  localizeElementText("emailAddressLabel", "emailAddress");
  localizeElementPlaceholder("manualEmail", "emailPlaceholder");
  localizeElementText("improveDraftBtn", "improveDraft");
  localizeElementText("verificationCodeLabel", "verificationCode");
  localizeElementPlaceholder("otpCode", "verificationCodePlaceholder");

  const codeHelp = document.querySelector("#codeWrap small");
  if (codeHelp) codeHelp.textContent = t("verificationCodeHelp");

  localizeElementText("signinCopy", "signinCopy");

  const continueBtn = document.getElementById("continueOutlookBtn");
  if (continueBtn && !continueBtn.disabled) {
    continueBtn.textContent = pendingOtp ? t("verifyCode") : t("sendCode");
  }

  const smallNote = document.querySelector("#welcomeView .small-note");
  if (smallNote) smallNote.textContent = t("newToSnoooz");

  localizeElementText("draftModeLabel", "draftModeLabel");
  localizeSelectOption("mode", "ai", "useAi");
  localizeSelectOption("mode", "template", "useTemplate");

  localizeElementText("instructionsLabel", "instructions");
  localizeElementPlaceholder("instructions", "instructionsPlaceholder");

  localizeElementText("toneLabel", "tone");
  localizeSelectOption("tone", "professional", "toneProfessional");
  localizeSelectOption("tone", "friendly", "toneFriendly");
  localizeSelectOption("tone", "concise", "toneConcise");
  localizeSelectOption("tone", "formal", "toneFormal");
  localizeSelectOption("tone", "empathetic", "toneEmpathetic");

  localizeElementText("lengthLabel", "length");
  localizeSelectOption("length", "short", "lengthShort");
  localizeSelectOption("length", "normal", "lengthNormal");
  localizeSelectOption("length", "detailed", "lengthDetailed");

  localizeElementText("useKnowledgebaseLabel", "useKnowledgebase");

  localizeElementText("createDraftBtn", "createDraft");
  localizeElementText("openWorkspaceBtn", "openWorkspace");
  localizeElementText("logoutBtn", "logout");
  localizeElementText("connectedAccountLabel", "connectedAccount");

  const debugBtn = document.getElementById("debugToggleBtn");
  const debugPanel = document.getElementById("welcomeDebug");

  if (debugBtn && debugPanel) {
    debugBtn.textContent = debugPanel.classList.contains("hidden")
      ? t("debug")
      : t("hideDebug");
  }

  const warning = document.getElementById("oldBrowserWarning");
  if (warning && !warning.classList.contains("hidden")) {
    warning.textContent = t("oldBrowserWarning");
  }
}

async function handleImproveDraft() {
  const button = document.getElementById("improveDraftBtn");

  const state = sessionBundle?.state || sessionBundle;

  if (!state?.session) {
    showWelcome();
    return;
  }

  if (button) {
    button.disabled = true;
    button.textContent = t("improvingDraft");
  }

  setStatus(t("improvingDraft"));

  try {
    let selectedText = "";

    try {
      selectedText = await getSelectedTextAsync();
    } catch (err) {
      console.warn("Could not read selected text:", err);
    }

    selectedText = String(selectedText || "").trim();

    let sourceText = selectedText;
    let replaceMode = "selection";
    let context = {};

    if (!sourceText) {
      context = await getComposeContext().catch(function () {
        return {};
      });

      const bodyText = await getComposeBodyTextAsync();
      sourceText = extractEditableDraftOnly(bodyText || context?.draft || "");
      replaceMode = "body";
    } else {
      context = await getComposeContext().catch(function () {
        return {};
      });
    }

    sourceText = String(sourceText || "").trim();

    console.log("Improve debug:", {
      selectedTextLength: selectedText.length,
      sourceTextLength: sourceText.length,
      replaceMode,
      sourceTextPreview: sourceText.slice(0, 300)
    });

    if (!sourceText) {
      setStatus(t("errorNothingToImprove"), true);
      return;
    }

    const result = await improveEmailText({
      draft: sourceText,
      subject: context?.subject || "",
      provider: context?.provider || "outlook-addin",
      improveMode: replaceMode,
      tone: document.getElementById("tone")?.value || "professional",
      length: document.getElementById("length")?.value || "normal"
    }, sessionBundle);

    const improvedText = String(result?.improvedText || "").trim();

    if (!improvedText) {
      throw new Error(t("errorImproveFailed"));
    }

    if (looksLikeAiRefusal(improvedText)) {
      throw new Error(t("errorNothingToImprove"));
    }

    if (replaceMode === "selection") {
      await replaceSelectedTextAsync(improvedText);
    } else {
      await replaceBodyHtmlAsync(textToHtmlForCompose(improvedText));
    }

    setStatus(t("statusImprovedDraft"), false, 3000);
  } catch (err) {
    console.error(err);

    setStatus(err.message || t("errorImproveFailed"), true);

    const debug = document.getElementById("debug");
    if (debug) {
      debug.textContent = JSON.stringify({
        message: err.message,
        code: err.code,
        status: err.status,
        data: err.data,
        stack: err.stack
      }, null, 2);
    }
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = t("improveDraft");
    }
  }
}

function looksLikeAiRefusal(text = "") {
  const value = String(text || "").toLowerCase();

  return (
    value.indexOf("please paste") >= 0 ||
    value.indexOf("provide the text") >= 0 ||
    value.indexOf("need you to provide") >= 0 ||
    value.indexOf("paste the content") >= 0
  );
}

function textToHtmlForCompose(text = "") {
  return String(text || "")
    .replace(/\r/g, "")
    .split(/\n{2,}/)
    .map(function (paragraph) {
      return "<p>" + escapeHtmlForCompose(paragraph).replace(/\n/g, "<br>") + "</p>";
    })
    .join("");
}

function escapeHtmlForCompose(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function bindEvents() {
  document
    .getElementById("language")
    ?.addEventListener("change", function (event) {
      setLanguage(event.target.value);
      applyTranslations();
      renderTemplates(sessionBundle?.templates || []);
      handleModeChange();
    });

  document
    .getElementById("improveDraftBtn")
    ?.addEventListener("click", handleImproveDraft);

  document
    .getElementById("continueOutlookBtn")
    ?.addEventListener("click", handleContinueWithOutlook);

  document
    .getElementById("region")
    ?.addEventListener("change", (event) => {
      saveRegion(event.target.value);
    });

  document
    .getElementById("logoutBtn")
    ?.addEventListener("click", handleLogout);

  document
    .getElementById("mode")
    ?.addEventListener("change", handleModeChange);

  document
    .getElementById("createDraftBtn")
    ?.addEventListener("click", handleCreateDraft);

  document
    .getElementById("openWorkspaceBtn")
    ?.addEventListener("click", handleOpenWorkspace);

  document
    .getElementById("debugToggleBtn")
    ?.addEventListener("click", handleToggleDebug);
}

function handleOpenWorkspace() {
  const state = sessionBundle?.state || sessionBundle;
  const region = state?.region || "us";

  const baseUrl = REGIONS[region]?.workspaceUrl || "https://app.snoooz.ai";

  window.open(baseUrl, "_blank", "noopener,noreferrer");
}

function getOutlookDiagnostics() {
  const office = window.Office;
  const mailbox = office?.context?.mailbox;
  const diagnostics = mailbox?.diagnostics || {};

  return {
    officeReady: !!office,
    hasOnReady: !!office?.onReady,
    hasContext: !!office?.context,
    hostName: diagnostics.hostName || "",
    hostVersion: diagnostics.hostVersion || "",
    platform: diagnostics.platform || "",
    userAgent: navigator.userAgent,
    hasMailbox: !!mailbox,
    hasUserProfile: !!mailbox?.userProfile,
    emailAddress: mailbox?.userProfile?.emailAddress || "",
    displayName: mailbox?.userProfile?.displayName || "",
    localStorageWorks: testLocalStorage()
  };
}

function resetOtpState() {
  pendingOtp = null;
  document.getElementById("manualEmail")?.removeAttribute("disabled");
  document.getElementById("region")?.removeAttribute("disabled");
  document.getElementById("codeWrap")?.classList.add("hidden");
  document.getElementById("codeSentNotice")?.classList.add("hidden");

  const notice = document.getElementById("codeSentNotice");
  if (notice) notice.textContent = "";

  const codeInput = document.getElementById("otpCode");
  if (codeInput) codeInput.value = "";
}

function testLocalStorage() {
  try {
    const key = "__snoooz_storage_test__";
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function showWelcome() {

  document.getElementById("welcomeView")?.classList.remove("hidden");
  document.getElementById("draftView")?.classList.add("hidden");

  const regionSelect = document.getElementById("region");
  const savedRegion = getSavedRegion();

  if (regionSelect && ["us", "eu"].includes(savedRegion)) {
    regionSelect.value = savedRegion;
  }

  const profile = getOfficeMailboxProfile();
  const manualEmailWrap = document.getElementById("manualEmailWrap");
  const manualEmailInput = document.getElementById("manualEmail");

  manualEmailWrap?.classList.remove("hidden");

  if (manualEmailInput && profile.email && !manualEmailInput.value) {
    manualEmailInput.value = profile.email;
  }

  renderAppVersion();
}

async function showDraftView(bundle) {
  document.getElementById("welcomeView")?.classList.add("hidden");
  document.getElementById("draftView")?.classList.remove("hidden");

  const state = bundle?.state || bundle;
  const session = state?.session;
  const region = state?.region || "us";

  setAccountStatus(
    `${session?.email || "Snoooz user"} · ${REGIONS[region]?.label || region}`
  );

  renderTemplates(bundle?.templates || []);
  handleModeChange();

}

async function handleContinueWithOutlook() {
  const button = document.getElementById("continueOutlookBtn");
  const region = document.getElementById("region")?.value || "us";
  const email = document.getElementById("manualEmail")?.value || "";
  const code = document.getElementById("otpCode")?.value || "";

  saveRegion(region);

  if (button) {
    button.disabled = true;
  }

  clearWelcomeError();

  try {
    if (!pendingOtp) {
      if (button) button.textContent = t("sendingCode");

      pendingOtp = await requestOutlookSigninCode(region, email);

      document.getElementById("manualEmail")?.setAttribute("disabled", "disabled");
      document.getElementById("region")?.setAttribute("disabled", "disabled");
      document.getElementById("codeWrap")?.classList.remove("hidden");

      if (button) button.textContent = t("verifyCode");
      setCodeSentNotice(pendingOtp.email);

      return;
    }

    if (!/^\d{6}$/.test(String(code).trim())) {
      throw Object.assign(new Error(t("errorInvalidCode")), {
        code: "INVALID_CODE"
      });
    }

    if (button) button.textContent = t("verifying");

    sessionBundle = await verifyOutlookSigninCode({
      region,
      email: pendingOtp.email,
      otpId: pendingOtp.otpId,
      code
    });

    pendingOtp = null;
    await showDraftView(sessionBundle);
  } catch (err) {
    console.error(err);

    if (err.code === "CODE_EXPIRED") {
      pendingOtp = null;
      document.getElementById("codeWrap")?.classList.add("hidden");
      setWelcomeError(t("errorCodeExpired"));
    } else if (err.code === "CODE_INVALID") {
      setWelcomeError(t("errorCodeIncorrect"));
    } else {
      setWelcomeError(err.message || t("errorCouldNotConnect"));
    }

  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = pendingOtp ? t("verifyCode") : t("sendCode");
    }
  }
}

async function handleLogout() {
  await logoutSession();
  sessionBundle = null;
  resetOtpState();
  showWelcome();
}

async function handleModeChange() {
  const mode = document.getElementById("mode")?.value || "ai";
  const templateWrap = document.getElementById("templateWrap");
  const aiWrap = document.getElementById("aiWrap");

  setHidden(templateWrap, mode !== "template");
  setHidden(aiWrap, mode === "template");


  if (mode === "template") {
    if (!sessionBundle) {
      document.getElementById("templateId").innerHTML =
        `<option value="">${t("reconnectToLoadTemplates")}</option>`;
      return;
    }
    await ensureTemplatesLoaded({ force: true });
  }
}

async function ensureTemplatesLoaded({ force = false } = {}) {
  const templateEl = document.getElementById("templateId");

  if (!templateEl) {
    console.warn("templateId element not found.");
    return;
  }

  const state = sessionBundle?.state || sessionBundle;
  const session = state?.session;
  const region = state?.region || "us";

  console.log("ensureTemplatesLoaded — sessionBundle dump:", JSON.stringify(sessionBundle, null, 2));

  if (!session?.companyId || !session?.userId) {
    console.warn("Bailing: missing companyId or userId", { session, sessionBundle });
    templateEl.innerHTML = `<option value="">${t("reconnectToLoadTemplates")}</option>`;
    return;
  }

  console.log("ensureTemplatesLoaded called:", {
    force,
    region,
    companyId: session?.companyId,
    userId: session?.userId,
    currentOptions: Array.from(templateEl.options || []).map(o => ({
      value: o.value,
      text: o.textContent
    }))
  });

  const debug = document.getElementById("debug");


  if (!session?.companyId || !session?.userId) {
    templateEl.innerHTML = `<option value="">${t("loadingTemplates")}</option>`;
    setStatus(t("reconnectToLoadTemplates"), true);
    return;
  }

  if (!force) {
    const existingTemplates = Array.from(templateEl.options || []).filter(option => option.value);
    if (existingTemplates.length > 0) return;
  }

  templateEl.innerHTML = `<option value="">${t("loadingTemplates")}</option>`;

  try {
    const templates = await getExtensionTemplates(session, region);

    sessionBundle = {
      ...(sessionBundle || {}),
      templates
    };

    renderTemplates(templates);

    if (!templates.length) {
      templateEl.innerHTML = `<option value="">${t("noTemplatesFound")}</option>`;
    }
  } catch (err) {
    console.error("Template loading error:", err);

    templateEl.innerHTML = `<option value="">${t("couldNotLoadTemplates")}</option>`;

    if (debug) {
      debug.textContent = JSON.stringify({
        message: err.message,
        status: err.status,
        data: err.data
      }, null, 2);
    }

    setStatus(err.message || t("couldNotLoadTemplates"), true);
  }
}

function renderTemplates(templates = []) {
  const templateEl = document.getElementById("templateId");
  if (!templateEl) return;

  if (!templates.length) {
    templateEl.innerHTML = `<option value="">${t("noTemplatesFound")}</option>`;
    return;
  }

  templateEl.innerHTML = `<option value="">${t("selectTemplate")}</option>`;

  for (const template of templates) {
    const option = document.createElement("option");
    option.value = template.id;
    option.textContent = template.title || t("untitledTemplate");
    templateEl.appendChild(option);
  }
}

function setAccountStatus(text) {
  const el = document.getElementById("accountStatus");
  if (el) el.textContent = text;
}


function setWelcomeError(message) {
  let el = document.getElementById("welcomeError");

  if (!el) {
    el = document.createElement("p");
    el.id = "welcomeError";
    el.className = "error-text";
    document.getElementById("welcomeView")?.appendChild(el);
  }

  el.textContent = message;
}

async function handleCreateDraft() {
  const button = document.getElementById("createDraftBtn");
  const mode = document.getElementById("mode")?.value || "ai";
  const templateId = document.getElementById("templateId")?.value || "";

  const state = sessionBundle?.state || sessionBundle;

  if (!state?.session) {
    showWelcome();
    return;
  }

  if (mode === "template" && !templateId) {
    setStatus(t("errorPleaseSelectTemplate"), true);
    return;
  }

  if (button) {
    button.disabled = true;
  }
  startMagicStatus(mode);

  try {
    let context = null;

    try {
      context = await getComposeContext();
    } catch (err) {
      console.warn("Could not read full Outlook compose context. Using fallback context.", err);

      const session = sessionBundle?.state?.session || {};

      context = {
        provider: "outlook",
        subject: "",
        to: [],
        cc: [],
        toPeople: [],
        ccPeople: [],
        draft: "",
        threadText: "",
        sender: {
          email: "",
          fullName: "",
          firstName: "",
          lastName: ""
        },
        user: {
          email: session.email || "",
          fullName: session.fullName || "",
          firstName: session.firstName || "",
          lastName: session.lastName || "",
          bookingLink: session.bookingLink || ""
        },
        compose: {
          signatureExists: false,
          existingBodyText: ""
        }
      };
    }

    console.log("Snoooz compose context used:", context);

    const draft = await createEmailDraft({
      ...context,
      mode,
      templateId: mode === "template" ? templateId : "",
      instructions: document.getElementById("instructions")?.value || "",
      useKnowledgebase: mode === "ai" && document.getElementById("useKnowledgebase")?.checked === true,
      tone: document.getElementById("tone")?.value || "professional",
      length: document.getElementById("length")?.value || "normal"
    }, sessionBundle);

    if (draft?.subject) {
      await setSubjectIfEmpty(draft.subject);
    }

    await setBodyHtml(draft?.bodyHtml || draft?.bodyText || "");

    setStatus(t("statusDraftAdded"), false, 3000);

  } catch (err) {
    console.error(err);
    setStatus(err.message || t("errorDraftCreationFailed"), true);

    const debug = document.getElementById("debug");
    if (debug) {
      debug.textContent = JSON.stringify({
        message: err.message,
        code: err.code,
        stack: err.stack
      }, null, 2);
    }
  } finally {
    stopMagicStatus();
    if (button) {
      button.disabled = false;
    }
  }
}

function startMagicStatus(mode) {
  const messages = mode === "template"
    ? [
      t("statusAddingTemplate"),
      t("statusReplacingFields"),
      t("statusAlmostReady")
    ]
    : [
      t("statusReading"),
      t("statusTone"),
      t("statusMagic"),
      t("statusPolishing"),
      t("statusAlmostReady")
    ];

  const status = document.getElementById("status");
  const text = document.getElementById("statusText");

  status?.classList.remove("hidden");

  let index = 0;
  if (text) text.textContent = messages[index];

  magicTimer = setInterval(() => {
    if (index >= messages.length - 1) {
      clearInterval(magicTimer);
      magicTimer = null;
      return;
    }

    index += 1;
    if (text) text.textContent = messages[index];
  }, 1400);
}

function stopMagicStatus() {
  if (magicTimer) {
    clearInterval(magicTimer);
    magicTimer = null;
  }
}

function setStatus(message, isError = false, autoHideMs = 0) {
  const status = document.getElementById("status");
  const text = document.getElementById("statusText");

  if (statusClearTimer) {
    clearTimeout(statusClearTimer);
    statusClearTimer = null;
  }

  status?.classList.remove("hidden");

  if (text) text.textContent = message;
  if (status) status.style.color = isError ? "#b42318" : "#0F5B44";

  if (autoHideMs > 0 && status) {
    statusClearTimer = setTimeout(() => {
      status.classList.add("hidden");
      if (text) text.textContent = "";
      statusClearTimer = null;
    }, autoHideMs);
  }
}

function clearWelcomeError() {
  removeElement(document.getElementById("welcomeError"));
}


function handleToggleDebug() {
  const panel = document.getElementById("welcomeDebug");
  const button = document.getElementById("debugToggleBtn");

  if (!panel) return;

  const willShow = panel.classList.contains("hidden");

  if (willShow) {
    panel.textContent = JSON.stringify(getOutlookDiagnostics(), null, 2);
    panel.classList.remove("hidden");
    if (button) button.textContent = t("hideDebug");
  } else {
    panel.classList.add("hidden");
    panel.textContent = "";
    if (button) button.textContent = t("debug");
  }
}

function setCodeSentNotice(email) {
  const el = document.getElementById("codeSentNotice");

  if (!el) return;

  el.textContent = t("codeSentTo", { email });
  el.classList.remove("hidden");
}